import type { Timestamp } from 'firebase/firestore';
import { isConstructedMetaSetCode, metaPeriodTimeRange } from '../../shared/metaPeriods.js';
import { legendDisplayName, legendIdentityFromRef } from './championIdentity';
import type {
  HistoryOutcome,
  MatchBestOf,
  MatchDiceRollEvent,
  MatchGameResult,
  MatchHistoryFilters,
  MatchMode,
  MatchParticipant,
  MatchParticipantMap,
  MatchParticipantSlot,
  MatchResult,
  MatchResultPlayer,
  MatchScoreEvent,
  MatchScoreMap,
  MatchSession,
  MatchStatsSummary,
  MatchTeamId,
  MatchTeamMap,
  UserMatchHistoryDiceRoll,
  UserMatchHistoryEntry,
} from '../types/match';

export const MIN_VICTORY_POINTS = 0;
export const MAX_VICTORY_POINTS = 99;
export const DEFAULT_TARGET_SCORE = 8;
export const MIN_TARGET_SCORE = 1;
export const MAX_TARGET_SCORE = 99;
export const TARGET_SCORE_OPTIONS = [8, 9, 10] as const;

/**
 * The standard target scores, plus the current value when a match was set up with
 * something off-list (Magma Chamber's 11, or an older saved result).
 */
export function targetScoreOptionsIncluding(current: number): number[] {
  return TARGET_SCORE_OPTIONS.includes(current as (typeof TARGET_SCORE_OPTIONS)[number])
    ? [...TARGET_SCORE_OPTIONS]
    : [...TARGET_SCORE_OPTIONS, current];
}
export const SESSION_TTL_HOURS = 6;
export const COMPLETED_SESSION_GRACE_HOURS = 48;
export const EXPIRED_LIVE_SESSION_STATUSES = ['waiting', 'active', 'ending'] as const;
export type ExpiredLiveSessionStatus = (typeof EXPIRED_LIVE_SESSION_STATUSES)[number];

export function isExpiredLiveSession(
  session: { status?: string; expiresAt?: Timestamp },
  now = Date.now(),
): boolean {
  if (!session.expiresAt || session.expiresAt.toMillis() > now) return false;
  return EXPIRED_LIVE_SESSION_STATUSES.includes(session.status as ExpiredLiveSessionStatus);
}

export function isCompletedSessionPastGrace(
  session: { status?: string; endedAt?: Timestamp | null },
  now = Date.now(),
  graceHours = COMPLETED_SESSION_GRACE_HOURS,
): boolean {
  if (session.status !== 'completed' || !session.endedAt) return false;
  const graceMs = graceHours * 60 * 60 * 1000;
  return session.endedAt.toMillis() + graceMs <= now;
}

export function isExpiredShortMatchCode(
  data: { expiresAt?: Timestamp },
  now = Date.now(),
): boolean {
  return Boolean(data.expiresAt && data.expiresAt.toMillis() < now);
}
export const MATCH_ROUND_SECONDS = 60 * 60;
/** Max away time credited toward the local match clock on restore. */
export const LOCAL_MATCH_TIMER_AWAY_CREDIT_SECONDS = 30 * 60;
/** Beyond this away gap the timer auto-pauses at the saved clock instead of accruing time. */
export const LOCAL_MATCH_TIMER_AUTO_PAUSE_AWAY_SECONDS = 2 * 60 * 60;
export const DEV_MATCH_HISTORY_PREFIX = 'dev-match-history-';
export const LOCAL_MATCH_HISTORY_PREFIX = 'local-';
export const MANUAL_MATCH_DECK_VALUE = 'manual' as const;
export const NO_DECK_LABEL = 'No deck';

const UNLABELED_DECK_TITLES = new Set([
  'unknown deck',
  'unknown',
  'manual deck',
  'dev manual deck',
  'no deck',
  'no deck selected',
  'unlabeled',
  'untitled',
  'untitled deck',
  'your deck',
]);

export function isUnlabeledDeckTitle(label?: string | null): boolean {
  const normalized = label?.trim().toLowerCase() ?? '';
  return !normalized || UNLABELED_DECK_TITLES.has(normalized);
}

export function isUnknownMatchupLabel(label?: string | null): boolean {
  return isUnlabeledDeckTitle(label);
}

export function displayDeckLabel(label?: string | null): string {
  return isUnlabeledDeckTitle(label) ? NO_DECK_LABEL : label!.trim();
}

export const MATCH_SLOTS = ['A', 'B', 'C', 'D'] as const satisfies readonly MatchParticipantSlot[];
export const DEFAULT_TWO_VS_TWO_TEAMS: MatchTeamMap = {
  team1: ['A', 'C'],
  team2: ['B', 'D'],
};
export const MATCH_TEAM_IDS = ['team1', 'team2'] as const satisfies readonly MatchTeamId[];

export function deckFromMatchDeckValue<T extends { id: string }>(
  deckValue: string,
  decks: T[],
): T | null {
  if (!deckValue || deckValue === MANUAL_MATCH_DECK_VALUE) return null;
  return decks.find((deck) => deck.id === deckValue) ?? null;
}

export function hasMatchDeckSelection(
  deckValue: string,
  manualDeckLabel: string,
  legendName?: string | null,
): boolean {
  if (legendName?.trim()) return true;
  if (deckValue === MANUAL_MATCH_DECK_VALUE) return Boolean(manualDeckLabel.trim());
  return Boolean(deckValue);
}

export type LocalMatchTimerDraftSlice = {
  timerRunning: boolean;
  timerStartedAtMs: number | null;
  elapsedBeforeTimerStart: number;
  timerSavedAtMs: number;
};

export type LocalMatchTimerRestoreVariant = 'away' | 'awayCapped' | 'autoPaused';

export type ResolvedLocalMatchTimerRestore = {
  elapsedBeforeTimerStart: number;
  timerRunning: boolean;
  timerStartedAtMs: number | null;
  clockLabel: string;
  promptOpen: boolean;
  promptVariant: LocalMatchTimerRestoreVariant;
  awaySeconds: number;
  awaySecondsCredited: number;
};

export function matchClockLabelFromElapsedSeconds(elapsedSeconds: number): string {
  const remainingSeconds = MATCH_ROUND_SECONDS - elapsedSeconds;
  if (remainingSeconds === 0) return formatMatchClock(0);
  return remainingSeconds < 0
    ? `+${formatMatchClock(Math.abs(remainingSeconds))}`
    : formatMatchClock(remainingSeconds);
}

export function resolveRestoredLocalMatchTimer(
  draft: LocalMatchTimerDraftSlice,
  nowMs = Date.now(),
): ResolvedLocalMatchTimerRestore | null {
  if (draft.timerRunning !== true || typeof draft.timerStartedAtMs !== 'number') {
    return null;
  }

  const elapsedAtSave = draft.elapsedBeforeTimerStart + Math.max(
    0,
    Math.floor((draft.timerSavedAtMs - draft.timerStartedAtMs) / 1000),
  );
  const awaySeconds = Math.max(0, Math.floor((nowMs - draft.timerSavedAtMs) / 1000));

  if (awaySeconds >= LOCAL_MATCH_TIMER_AUTO_PAUSE_AWAY_SECONDS) {
    return {
      elapsedBeforeTimerStart: elapsedAtSave,
      timerRunning: false,
      timerStartedAtMs: null,
      clockLabel: matchClockLabelFromElapsedSeconds(elapsedAtSave),
      promptOpen: true,
      promptVariant: 'autoPaused',
      awaySeconds,
      awaySecondsCredited: 0,
    };
  }

  const awaySecondsCredited = Math.min(awaySeconds, LOCAL_MATCH_TIMER_AWAY_CREDIT_SECONDS);
  const restoredElapsedSeconds = elapsedAtSave + awaySecondsCredited;

  return {
    elapsedBeforeTimerStart: restoredElapsedSeconds,
    timerRunning: false,
    timerStartedAtMs: null,
    clockLabel: matchClockLabelFromElapsedSeconds(restoredElapsedSeconds),
    promptOpen: true,
    promptVariant: awaySeconds > awaySecondsCredited ? 'awayCapped' : 'away',
    awaySeconds,
    awaySecondsCredited,
  };
}

export type SharedMatchTimerSlice = {
  timerRunning?: boolean;
  timerStartedAt?: Timestamp | null;
  timerAccumulatedSeconds?: number;
  updatedAt?: Timestamp;
};

/**
 * Shared-session variant of the local timer-restore policy.
 *
 * A shared session's timer is declarative Firestore state (`timerStartedAt`
 * + accumulated seconds), so a session everyone walked away from keeps
 * "running" on wall clock and greets whoever reconnects with hours of
 * overtime. The dormancy signal is `updatedAt`: every score/XP/timer/settings
 * write touches it, so a running timer with a stale `updatedAt` means nobody
 * has interacted with the match — the shared analogue of a stale local draft.
 *
 * Deliberate difference from the local policy: short gaps return null instead
 * of prompting. Reconnects are routine in shared sessions (tab switches,
 * phone lock between turns) and the opponent may be actively mid-game, so we
 * only intervene once the gap exceeds the away-credit cap. Time between
 * `timerStartedAt` and the last activity is real play and is credited in
 * full; the dormant tail is capped/zeroed by the same math as local restores
 * (via {@link resolveRestoredLocalMatchTimer} — do not fork the arithmetic).
 */
export function resolveRestoredSharedMatchTimer(
  session: SharedMatchTimerSlice,
  nowMs = Date.now(),
): ResolvedLocalMatchTimerRestore | null {
  if (session.timerRunning !== true || !session.timerStartedAt) return null;
  const startedAtMs = session.timerStartedAt.toMillis();
  const lastActivityMs = Math.max(startedAtMs, session.updatedAt?.toMillis() ?? 0);
  const resolved = resolveRestoredLocalMatchTimer(
    {
      timerRunning: true,
      timerStartedAtMs: startedAtMs,
      elapsedBeforeTimerStart: session.timerAccumulatedSeconds ?? 0,
      timerSavedAtMs: lastActivityMs,
    },
    nowMs,
  );
  if (!resolved || resolved.promptVariant === 'away') return null;
  return resolved;
}

export function clampVictoryPoints(value: number): number {
  return Math.max(MIN_VICTORY_POINTS, Math.min(MAX_VICTORY_POINTS, value));
}

export function clampTargetScore(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_TARGET_SCORE;
  return Math.max(MIN_TARGET_SCORE, Math.min(MAX_TARGET_SCORE, Math.round(value)));
}

export function clampGameWins(current: number, delta: number, bestOf: number): number {
  const maxWins = Math.ceil(bestOf / 2);
  return Math.max(0, Math.min(maxWins, current + delta));
}

export function matchModeForFormat(format: string | null | undefined): MatchMode {
  if (format === 'two-vs-two' || format === '2v2') return 'twoVsTwo';
  if (format === 'side-event' || format === 'free-for-all-4' || format === 'freeForAll4') return 'freeForAll4';
  return 'standard1v1';
}

export function matchModeForSession(session: Pick<MatchSession, 'matchMode' | 'format' | 'maxPlayers'> | null | undefined): MatchMode {
  if (!session) return 'standard1v1';
  if (session.matchMode) return session.matchMode;
  const fromFormat = matchModeForFormat(session.format);
  if (fromFormat !== 'standard1v1') return fromFormat;
  return session.maxPlayers === 4 ? 'freeForAll4' : 'standard1v1';
}

export function targetScoreForMatchMode(mode: MatchMode): number {
  return mode === 'twoVsTwo' ? 11 : DEFAULT_TARGET_SCORE;
}

export function requiredPlayerCountForMatchMode(mode: MatchMode): 2 | 4 {
  return mode === 'standard1v1' ? 2 : 4;
}

export function slotsForMatchMode(mode: MatchMode): MatchParticipantSlot[] {
  return MATCH_SLOTS.slice(0, requiredPlayerCountForMatchMode(mode));
}

export function activeParticipantSlots(
  participants: Partial<Record<MatchParticipantSlot, MatchParticipant>> | undefined,
  mode: MatchMode = 'standard1v1',
): MatchParticipantSlot[] {
  const allowed = slotsForMatchMode(mode);
  return allowed.filter((slot) => Boolean(participants?.[slot]));
}

export function isSessionReady(session: MatchSession): boolean {
  const mode = matchModeForSession(session);
  return activeParticipantSlots(session.participants, mode).length >= requiredPlayerCountForMatchMode(mode);
}

export function teamsForMatchMode(mode: MatchMode, teams?: MatchTeamMap | null): MatchTeamMap | null {
  if (mode !== 'twoVsTwo') return null;
  return teams ?? DEFAULT_TWO_VS_TWO_TEAMS;
}

export function labelForTeamId(teamId: MatchTeamId): string {
  return teamId === 'team1' ? 'Team 1' : 'Team 2';
}

export function teamSlots(teamId: MatchTeamId, teams?: MatchTeamMap | null): MatchParticipantSlot[] {
  return teams?.[teamId] ?? DEFAULT_TWO_VS_TWO_TEAMS[teamId];
}

export function teamRepresentativeSlot(teamId: MatchTeamId, teams?: MatchTeamMap | null): MatchParticipantSlot {
  return teamSlots(teamId, teams)[0] ?? (teamId === 'team1' ? 'A' : 'B');
}

export function representativeSlotForTeamSlot(slot: MatchParticipantSlot, teams?: MatchTeamMap | null): MatchParticipantSlot {
  const teamId = teamIdForSlot(slot, teams);
  return teamId ? teamRepresentativeSlot(teamId, teams) : slot;
}

export function teamIdForSlot(slot: MatchParticipantSlot, teams?: MatchTeamMap | null): MatchTeamId | null {
  const resolvedTeams = teams ?? DEFAULT_TWO_VS_TWO_TEAMS;
  if (resolvedTeams.team1.includes(slot)) return 'team1';
  if (resolvedTeams.team2.includes(slot)) return 'team2';
  return null;
}

export function opposingTeamId(teamId: MatchTeamId): MatchTeamId {
  return teamId === 'team1' ? 'team2' : 'team1';
}

export function participantMapFromSlots(participants: Partial<Record<MatchParticipantSlot, MatchParticipant>>): MatchParticipantMap {
  if (!participants.A) throw new Error('Match requires player A.');
  return participants as MatchParticipantMap;
}

export function scoreMapForParticipants(participants: Partial<Record<MatchParticipantSlot, MatchParticipant>>): MatchScoreMap {
  return MATCH_SLOTS.reduce<MatchScoreMap>((scores, slot) => {
    const participant = participants[slot];
    if (participant) scores[slot] = participant.victoryPoints;
    return scores;
  }, {});
}

export function teamScore(scores: MatchScoreMap | undefined, teamId: MatchTeamId, teams?: MatchTeamMap | null): number {
  return teamSlots(teamId, teams).reduce((total, slot) => total + (scores?.[slot] ?? 0), 0);
}

export function teamGameWins(
  players: Partial<Record<MatchParticipantSlot, Pick<MatchParticipant | MatchResultPlayer, 'gameWins'>>> | undefined,
  teamId: MatchTeamId,
  teams?: MatchTeamMap | null,
): number {
  return Math.max(0, ...teamSlots(teamId, teams).map((slot) => players?.[slot]?.gameWins ?? 0));
}

/** Prefer `@slug` for compact UI labels; fall back to display name. */
export function matchPlayerLabel(
  player: Pick<MatchParticipant | MatchResultPlayer, 'displayName' | 'slug'> | null | undefined,
  fallback = 'Player',
): string {
  const slug = player?.slug?.trim();
  if (slug) return `@${slug}`;
  const name = player?.displayName?.trim();
  return name || fallback;
}

export function teamDisplayName(
  players: Partial<Record<MatchParticipantSlot, Pick<MatchParticipant | MatchResultPlayer, 'displayName' | 'slug'>>> | undefined,
  teamId: MatchTeamId,
  teams?: MatchTeamMap | null,
): string {
  const names = teamSlots(teamId, teams)
    .map((slot) => {
      const player = players?.[slot];
      if (!player) return '';
      return matchPlayerLabel(player, '');
    })
    .filter(Boolean);
  return names.length ? names.join(' + ') : labelForTeamId(teamId);
}

export function resetVictoryPointsForParticipants(participants: MatchParticipantMap): MatchParticipantMap {
  return participantMapFromSlots(
    MATCH_SLOTS.reduce<Partial<Record<MatchParticipantSlot, MatchParticipant>>>((next, slot) => {
      const participant = participants[slot];
      if (participant) next[slot] = { ...participant, victoryPoints: 0 };
      return next;
    }, {}),
  );
}

export function resultPlayersFromParticipants(participants: MatchParticipantMap): Record<MatchParticipantSlot, MatchResultPlayer> {
  return MATCH_SLOTS.reduce<Partial<Record<MatchParticipantSlot, MatchResultPlayer>>>((players, slot) => {
    const participant = participants[slot];
    if (participant) players[slot] = toResultPlayer(participant);
    return players;
  }, {}) as Record<MatchParticipantSlot, MatchResultPlayer>;
}

export function winnerTeamIdForWinnerSlot(mode: MatchMode, winnerSlot: MatchParticipantSlot, teams?: MatchTeamMap | null): MatchTeamId | null {
  return mode === 'twoVsTwo' ? teamIdForSlot(winnerSlot, teams) : null;
}

export function matchResultIsDraw(result: Pick<MatchResult, 'winnerSlot' | 'isDraw'>): boolean {
  return result.isDraw === true || result.winnerSlot == null;
}

export function historyEntryIsDraw(entry: Pick<UserMatchHistoryEntry, 'didWin' | 'didDraw'>): boolean {
  return entry.didDraw === true || entry.didWin == null;
}

export function historyEntryOutcome(entry: Pick<UserMatchHistoryEntry, 'didWin' | 'didDraw'>): HistoryOutcome {
  if (historyEntryIsDraw(entry)) return 'draw';
  return entry.didWin ? 'win' : 'loss';
}

export function historyEntryOutcomeLabel(entry: Pick<UserMatchHistoryEntry, 'didWin' | 'didDraw'>): string {
  const outcome = historyEntryOutcome(entry);
  return outcome === 'win' ? 'Won' : outcome === 'loss' ? 'Lost' : 'Tied';
}

export function historyEntryScore(entry: Pick<UserMatchHistoryEntry, 'myGameWins' | 'opponentGameWins' | 'didWin' | 'didDraw'>): {
  mine: number;
  opponent: number;
} {
  if (entry.myGameWins != null && entry.opponentGameWins != null) {
    return { mine: entry.myGameWins, opponent: entry.opponentGameWins };
  }
  const outcome = historyEntryOutcome(entry);
  if (outcome === 'draw') return { mine: 0, opponent: 0 };
  return outcome === 'win' ? { mine: 1, opponent: 0 } : { mine: 0, opponent: 1 };
}

export function historyEntryScoreLabel(
  entry: Pick<UserMatchHistoryEntry, 'myGameWins' | 'opponentGameWins' | 'didWin' | 'didDraw'>,
): string {
  const score = historyEntryScore(entry);
  return `${score.mine}-${score.opponent}`;
}

export function timestampMillis(value: Timestamp | undefined): number {
  return value?.toMillis?.() ?? 0;
}

export function otherSlot(slot: MatchParticipantSlot): MatchParticipantSlot {
  if (slot !== 'A' && slot !== 'B') {
    throw new Error(`Slot ${slot} does not have a single 1v1 opponent.`);
  }
  return slot === 'A' ? 'B' : 'A';
}

export function matchWinsNeeded(bestOf: number): number {
  return Math.ceil(bestOf / 2);
}

export function formatMatchClock(totalSeconds: number): string {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function matchHistoryEntryHasDetails(entry: UserMatchHistoryEntry): boolean {
  return !entry.matchResultId.startsWith(DEV_MATCH_HISTORY_PREFIX)
    && !entry.matchResultId.startsWith(LOCAL_MATCH_HISTORY_PREFIX);
}

export function diceRollForHistoryEntry(
  roll: MatchDiceRollEvent,
  mySlot: MatchParticipantSlot,
): UserMatchHistoryDiceRoll {
  const rolls = roll.rolls ?? { A: roll.rollA, B: roll.rollB };
  const myRoll = rolls[mySlot] ?? (mySlot === 'A' ? roll.rollA : mySlot === 'B' ? roll.rollB : 0);
  const opponentRolls = MATCH_SLOTS.reduce<Partial<Record<MatchParticipantSlot, number>>>((out, slot) => {
    if (slot !== mySlot && rolls[slot] != null) out[slot] = rolls[slot];
    return out;
  }, {});
  const opponentRoll = opponentRolls.A ?? opponentRolls.B ?? opponentRolls.C ?? opponentRolls.D ?? 0;
  return {
    diceType: roll.diceType,
    myRoll,
    opponentRoll,
    opponentRolls,
    iPlayFirst: roll.firstPlayerSlot === mySlot,
  };
}

export function formatHistoryDiceRoll(roll: UserMatchHistoryDiceRoll): string {
  const firstLabel = roll.iPlayFirst ? 'You play first' : 'Opponent plays first';
  return `${roll.diceType}: ${roll.myRoll}-${roll.opponentRoll} · ${firstLabel}`;
}

export function currentUserSlot(
  session: MatchSession | null,
  uid?: string | null,
): MatchParticipantSlot | null {
  if (!session || !uid) return null;
  for (const slot of MATCH_SLOTS) {
    if (session.participants[slot]?.uid === uid) return slot;
  }
  return null;
}

export function normalizeMatchShortCode(shortCode: string): string {
  const clean = shortCode.trim().toUpperCase();
  if (!clean) throw new Error('Enter a join code.');
  return clean;
}

export function resolveShortCodeLookup(
  data: { sessionId?: string; expiresAt?: Timestamp } | undefined,
  now = Date.now(),
): string {
  if (!data) throw new Error('Join code not found.');
  if (data.expiresAt && data.expiresAt.toMillis() < now) {
    throw new Error('That join code has expired.');
  }
  if (!data.sessionId) throw new Error('Join code is missing a session.');
  return data.sessionId;
}

export function toResultPlayer(participant: MatchParticipant): MatchResultPlayer {
  return {
    slot: participant.slot,
    uid: participant.uid,
    isGuest: participant.isGuest,
    displayName: participant.displayName,
    slug: participant.slug ?? null,
    photoURL: participant.photoURL ?? null,
    deckId: participant.deckId ?? null,
    deckTitle: participant.deckTitle ?? null,
    deckOwnerId: participant.deckOwnerId ?? null,
    manualDeckLabel: participant.manualDeckLabel ?? null,
    legendName: participant.legendName ?? null,
    legendImageUrl: participant.legendImageUrl ?? null,
    gameWins: participant.gameWins,
    finalVictoryPoints: participant.victoryPoints,
  };
}

export function historyEntryForUser(
  uid: string,
  result: MatchResult,
  mySlot: MatchParticipantSlot,
): UserMatchHistoryEntry {
  const mode = result.matchMode ?? matchModeForFormat(result.format);
  const me = result.players[mySlot];
  if (!me) throw new Error(`Missing result player for slot ${mySlot}.`);
  const teams = teamsForMatchMode(mode, result.teams);
  const myTeamId = teams ? teamIdForSlot(mySlot, teams) : null;
  const opponentTeamId = myTeamId ? opposingTeamId(myTeamId) : null;
  const teammateSlot = teams?.[myTeamId ?? 'team1']?.find((slot) => slot !== mySlot) ?? null;
  const teammate = teammateSlot ? result.players[teammateSlot] : null;
  const opponentSlots = MATCH_SLOTS.filter((slot) => slot !== mySlot && result.players[slot] && (!teams || teamIdForSlot(slot, teams) !== myTeamId));
  const primaryOpponentSlot = opponentSlots[0] ?? MATCH_SLOTS.find((slot) => slot !== mySlot && result.players[slot]);
  const opponent = primaryOpponentSlot ? result.players[primaryOpponentSlot] : null;
  const opponentDeckLabel = opponent?.deckTitle || opponent?.manualDeckLabel || opponent?.legendName || NO_DECK_LABEL;
  const opponentLegendName = opponent?.legendName
    || (legendPrintIdentity(opponentDeckLabel) && /[,]|(\s[-–—]\s)/.test(opponentDeckLabel)
      ? opponentDeckLabel
      : null);
  const opponentNames = opponentSlots.map((slot) => result.players[slot]?.displayName).filter(Boolean) as string[];
  const opponentDeckLabels = opponentSlots
    .map((slot) => {
      const player = result.players[slot];
      return player?.deckTitle || player?.manualDeckLabel || player?.legendName || null;
    })
    .filter(Boolean) as string[];
  const isDraw = matchResultIsDraw(result);
  const didWin = isDraw
    ? null
    : mode === 'twoVsTwo' && myTeamId
      ? result.winnerTeamId === myTeamId
      : result.winnerSlot === mySlot;
  const myGameWins = myTeamId ? teamGameWins(result.players, myTeamId, teams) : me.gameWins;
  const opponentGameWins = opponentTeamId ? teamGameWins(result.players, opponentTeamId, teams) : opponent?.gameWins ?? null;

  return {
    matchResultId: result.id,
    playedAt: result.playedAt,
    myUid: uid,
    opponentUid: opponent?.uid ?? null,
    opponentName: opponent?.displayName ?? (mode === 'freeForAll4' ? '4-player FFA table' : 'Unknown opponent'),
    myPhotoURL: me.photoURL ?? null,
    opponentPhotoURL: opponent?.photoURL ?? null,
    myDeckId: me.deckId ?? null,
    myDeckTitle: me.deckTitle || me.manualDeckLabel || me.legendName || NO_DECK_LABEL,
    myDeckCoverUrl: me.legendImageUrl ?? null,
    myGameWins,
    opponentDeckId: opponent?.deckId ?? null,
    opponentDeckLabel,
    opponentLegendName,
    opponentDeckCoverUrl: opponent?.legendImageUrl ?? null,
    opponentGameWins,
    teammateUid: teammate?.uid ?? null,
    teammateName: teammate?.displayName ?? null,
    teammateDeckLabel: teammate?.deckTitle || teammate?.manualDeckLabel || teammate?.legendName || null,
    opponentNames,
    opponentDeckLabels,
    matchMode: mode,
    teamId: myTeamId,
    winnerTeamId: result.winnerTeamId ?? null,
    didWin,
    didDraw: isDraw,
    winnerSlot: result.winnerSlot,
    bestOf: result.bestOf,
    format: result.format,
    storeName: result.storeName ?? null,
    eventName: result.eventName ?? null,
    eventId: result.eventId ?? null,
    eventRound: result.eventRound ?? null,
    diceRoll: result.diceRoll ? diceRollForHistoryEntry(result.diceRoll, mySlot) : null,
  };
}

export interface LocalMatchRevisionWriteInput {
  matchResultId: string;
  userUid: string;
  winnerSlot: MatchParticipantSlot | null;
  isDraw: boolean;
  myGameWins: number;
  opponentGameWins: number;
  playerAVictoryPoints: number;
  playerBVictoryPoints: number;
  gameResults: MatchGameResult[];
  scoreEvents: MatchScoreEvent[];
  durationSeconds: number | null;
  matchMode?: MatchMode;
  teams?: MatchTeamMap | null;
  winnerTeamId?: MatchTeamId | null;
}

export interface LocalMatchRevisionWrite {
  matchResultId: string;
  result: {
    winnerSlot: MatchParticipantSlot | null;
    loserSlot: MatchParticipantSlot | null;
    winnerTeamId: MatchTeamId | null;
    loserTeamId: MatchTeamId | null;
    isDraw: boolean;
    winnerUid: string | null;
    loserUid: string | null;
    gameResults: MatchGameResult[];
    scoreEvents: MatchScoreEvent[];
    durationSeconds: number | null;
    'players.A.gameWins': number;
    'players.A.finalVictoryPoints': number;
    'players.B.gameWins': number;
    'players.B.finalVictoryPoints': number;
  };
  history: Pick<
    UserMatchHistoryEntry,
    'didWin' | 'didDraw' | 'winnerSlot' | 'winnerTeamId' | 'myGameWins' | 'opponentGameWins'
  >;
}

/**
 * Fields to overwrite on an existing local result when a round is rescored.
 * The id is the one already stored on the round — this never mints a new row.
 */
export function localMatchRevisionWrite(input: LocalMatchRevisionWriteInput): LocalMatchRevisionWrite {
  const mode = input.matchMode ?? 'standard1v1';
  const teams = teamsForMatchMode(mode, input.teams);
  const drawn = input.isDraw || input.winnerSlot == null;
  const winnerSlot = drawn ? null : input.winnerSlot;
  const winnerTeamId = drawn || !winnerSlot
    ? null
    : (input.winnerTeamId ?? winnerTeamIdForWinnerSlot(mode, winnerSlot, teams));
  const loserSlot = !drawn && winnerSlot && mode === 'standard1v1' ? otherSlot(winnerSlot) : null;
  const loserTeamId = winnerTeamId ? opposingTeamId(winnerTeamId) : null;
  const didWin = drawn
    ? null
    : mode === 'twoVsTwo' && teams && winnerTeamId
      ? winnerTeamId === teamIdForSlot('A', teams)
      : winnerSlot === 'A';

  return {
    matchResultId: input.matchResultId,
    result: {
      winnerSlot,
      loserSlot,
      winnerTeamId,
      loserTeamId,
      isDraw: drawn,
      winnerUid: !drawn && winnerSlot === 'A' ? input.userUid : null,
      loserUid: loserSlot === 'A' ? input.userUid : null,
      gameResults: input.gameResults,
      scoreEvents: input.scoreEvents,
      durationSeconds: input.durationSeconds,
      'players.A.gameWins': input.myGameWins,
      'players.A.finalVictoryPoints': input.playerAVictoryPoints,
      'players.B.gameWins': input.opponentGameWins,
      'players.B.finalVictoryPoints': input.playerBVictoryPoints,
    },
    history: {
      didWin,
      didDraw: drawn,
      winnerSlot,
      winnerTeamId,
      myGameWins: input.myGameWins,
      opponentGameWins: input.opponentGameWins,
    },
  };
}

const MATCH_HISTORY_RANGE_VALUES = ['7d', '30d', 'all'] as const;
const MATCH_HISTORY_RESULT_VALUES = ['all', 'wins', 'losses', 'draws'] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

export function parseBestOfFilter(value: string | null | undefined): MatchBestOf | 'all' {
  if (value === '1' || value === '3' || value === '5') return Number(value) as MatchBestOf;
  return 'all';
}

export function parseMatchHistoryFilters(params: { get: (key: string) => string | null }): MatchHistoryFilters {
  const range = params.get('range');
  const result = params.get('result');
  const meta = params.get('meta')?.trim().toUpperCase();
  return {
    deckId: params.get('deck') || undefined,
    legendIdentity: params.get('legend')?.trim().toLowerCase() || undefined,
    bestOf: parseBestOfFilter(params.get('bestOf')),
    format: params.get('format') || undefined,
    range: MATCH_HISTORY_RANGE_VALUES.includes(range as (typeof MATCH_HISTORY_RANGE_VALUES)[number])
      ? (range as MatchHistoryFilters['range'])
      : 'all',
    result: MATCH_HISTORY_RESULT_VALUES.includes(result as (typeof MATCH_HISTORY_RESULT_VALUES)[number])
      ? (result as MatchHistoryFilters['result'])
      : 'all',
    meta: meta && isConstructedMetaSetCode(meta) ? meta : undefined,
    eventId: params.get('event')?.trim() || undefined,
  };
}

export function matchHistoryFilterCount(filters: MatchHistoryFilters): number {
  let count = 0;
  if (filters.deckId) count += 1;
  if (filters.legendIdentity) count += 1;
  if (filters.bestOf && filters.bestOf !== 'all') count += 1;
  if (filters.format) count += 1;
  if (filters.range && filters.range !== 'all') count += 1;
  if (filters.result && filters.result !== 'all') count += 1;
  if (filters.meta) count += 1;
  if (filters.eventId) count += 1;
  return count;
}

/** Combined playedAt window for rolling date range + constructed meta period. */
export function matchHistoryPlayedAtWindow(
  filters: MatchHistoryFilters,
  now = Date.now(),
): { startMs: number | null; endMs: number | null } {
  let startMs: number | null = null;
  let endMs: number | null = null;

  if (filters.range === '7d') startMs = now - 7 * DAY_MS;
  else if (filters.range === '30d') startMs = now - 30 * DAY_MS;

  if (filters.meta) {
    const metaRange = metaPeriodTimeRange(filters.meta);
    if (metaRange) {
      startMs = startMs == null ? metaRange.startMs : Math.max(startMs, metaRange.startMs);
      if (metaRange.endMs != null) {
        endMs = endMs == null ? metaRange.endMs : Math.min(endMs, metaRange.endMs);
      }
    }
  }

  return { startMs, endMs };
}

export function deckIdsForLegendIdentity(
  decks: MatchLegendLookupDeck[],
  identity?: string | null,
): string[] {
  if (!identity) return [];
  return decks
    .filter((deck) => legendIdentityFromRef(deck.legend) === identity)
    .map((deck) => deck.id);
}

export function filterMatchHistoryEntries(
  entries: UserMatchHistoryEntry[],
  filters: MatchHistoryFilters,
  now = Date.now(),
  decks: MatchLegendLookupDeck[] = [],
): UserMatchHistoryEntry[] {
  const { startMs, endMs } = matchHistoryPlayedAtWindow(filters, now);
  const legendDeckIds = !filters.deckId && filters.legendIdentity
    ? new Set(deckIdsForLegendIdentity(decks, filters.legendIdentity))
    : null;

  return entries.filter((entry) => {
    if (filters.deckId && entry.myDeckId !== filters.deckId) return false;
    if (legendDeckIds && (!entry.myDeckId || !legendDeckIds.has(entry.myDeckId))) return false;
    const playedAt = timestampMillis(entry.playedAt);
    if (startMs != null && playedAt < startMs) return false;
    if (endMs != null && playedAt >= endMs) return false;
    if (filters.result === 'wins' && historyEntryOutcome(entry) !== 'win') return false;
    if (filters.result === 'losses' && historyEntryOutcome(entry) !== 'loss') return false;
    if (filters.result === 'draws' && historyEntryOutcome(entry) !== 'draw') return false;
    if (filters.bestOf && filters.bestOf !== 'all' && entry.bestOf !== filters.bestOf) return false;
    if (filters.format && entry.format !== filters.format) return false;
    if (filters.eventId && entry.eventId !== filters.eventId) return false;
    return true;
  });
}

export type MatchHistoryTimelineGroup =
  | { kind: 'match'; entry: UserMatchHistoryEntry }
  | {
      kind: 'event';
      eventId: string;
      eventName: string | null;
      entries: UserMatchHistoryEntry[];
    };

/**
 * Collapse consecutive history rows that share an event into one group.
 * A lone event match stays a regular row so a single bye or Swiss game is not
 * wrapped in a header. Groups keep newest-first placement in the timeline.
 */
export function groupMatchHistoryEntries(
  entries: UserMatchHistoryEntry[],
): MatchHistoryTimelineGroup[] {
  const items: MatchHistoryTimelineGroup[] = [];
  for (const entry of entries) {
    const eventId = entry.eventId?.trim();
    const last = items[items.length - 1];
    if (eventId && last?.kind === 'event' && last.eventId === eventId) {
      last.entries.push(entry);
      if (!last.eventName && entry.eventName) last.eventName = entry.eventName;
      continue;
    }
    if (eventId) {
      items.push({
        kind: 'event',
        eventId,
        eventName: entry.eventName ?? null,
        entries: [entry],
      });
      continue;
    }
    items.push({ kind: 'match', entry });
  }

  return items.flatMap((item) => {
    if (item.kind === 'event' && item.entries.length < 2) {
      return item.entries.map((entry) => ({ kind: 'match' as const, entry }));
    }
    return [item];
  });
}

export function sortEventGroupRounds(
  entries: UserMatchHistoryEntry[],
): UserMatchHistoryEntry[] {
  return [...entries].sort((a, b) => {
    const roundA = a.eventRound ?? Number.MAX_SAFE_INTEGER;
    const roundB = b.eventRound ?? Number.MAX_SAFE_INTEGER;
    if (roundA !== roundB) return roundA - roundB;
    return timestampMillis(a.playedAt) - timestampMillis(b.playedAt);
  });
}

export function eventGroupRecord(entries: UserMatchHistoryEntry[]): { wins: number; losses: number; draws: number } {
  let wins = 0;
  let losses = 0;
  let draws = 0;
  for (const entry of entries) {
    const outcome = historyEntryOutcome(entry);
    if (outcome === 'win') wins += 1;
    else if (outcome === 'loss') losses += 1;
    else draws += 1;
  }
  return { wins, losses, draws };
}

export const MIN_STAT_GAMES = 3;
export const MIN_GOING_FIRST_GAMES = 5;
export const MATCH_FORM_LENGTH = 10;

export type MatchStreakKind = 'win' | 'loss';

export interface MatchStreak {
  kind: MatchStreakKind;
  count: number;
}

export function calculateMatchStats(entries: UserMatchHistoryEntry[]): MatchStatsSummary {
  let wins = 0;
  let losses = 0;
  let draws = 0;
  for (const entry of entries) {
    const outcome = historyEntryOutcome(entry);
    if (outcome === 'win') wins += 1;
    else if (outcome === 'loss') losses += 1;
    else draws += 1;
  }
  const total = entries.length;
  return {
    wins,
    losses,
    draws,
    total,
    winRate: total > 0 ? wins / total : 0,
  };
}

export function hasEnoughStatGames(total: number, minGames = MIN_STAT_GAMES): boolean {
  return total >= minGames;
}

export function formatMatchWinRate(winRate: number, total: number, minGames = MIN_STAT_GAMES): string {
  if (total < minGames) return '--';
  return `${(winRate * 100).toFixed(1)}%`;
}

export function formatMatchRecord(stats: Pick<MatchStatsSummary, 'wins' | 'losses' | 'draws'>): string {
  return stats.draws > 0
    ? `${stats.wins}-${stats.losses}-${stats.draws}`
    : `${stats.wins}-${stats.losses}`;
}

export function formatMatchStreak(streak: MatchStreak): string {
  return `${streak.kind === 'win' ? 'W' : 'L'}${streak.count}`;
}

/** Consecutive current result from newest-first history. */
export function calculateMatchStreak(entries: UserMatchHistoryEntry[]): MatchStreak | null {
  if (entries.length === 0) return null;
  const first = historyEntryOutcome(entries[0]);
  if (first === 'draw') return null;
  const kind: MatchStreakKind = first;
  let count = 0;
  for (const entry of entries) {
    if (historyEntryOutcome(entry) !== kind) break;
    count += 1;
  }
  return { kind, count };
}

/** Newest-first W/L/D sequence for form pips. */
export function lastMatchResults(entries: UserMatchHistoryEntry[], count = MATCH_FORM_LENGTH): HistoryOutcome[] {
  return entries.slice(0, count).map(historyEntryOutcome);
}

export function groupStatsByGoingFirst(entries: UserMatchHistoryEntry[]) {
  const first: UserMatchHistoryEntry[] = [];
  const second: UserMatchHistoryEntry[] = [];
  for (const entry of entries) {
    if (!entry.diceRoll) continue;
    if (entry.diceRoll.iPlayFirst) first.push(entry);
    else second.push(entry);
  }
  return {
    first: calculateMatchStats(first),
    second: calculateMatchStats(second),
    rolled: first.length + second.length,
  };
}

export function sortRecordGroups<T extends { stats: MatchStatsSummary }>(groups: T[]): T[] {
  return [...groups].sort((a, b) => {
    if (b.stats.total !== a.stats.total) return b.stats.total - a.stats.total;
    return b.stats.winRate - a.stats.winRate;
  });
}

function firstCoverUrl(entries: UserMatchHistoryEntry[], key: 'myDeckCoverUrl' | 'opponentDeckCoverUrl'): string | null {
  return entries.find((entry) => entry[key])?.[key] ?? null;
}

export interface MatchLegendLookupDeck {
  id: string;
  title?: string | null;
  legend?: {
    name?: string | null;
    champion?: string | null;
    thumbnailUrl?: string | null;
    imageUrl?: string | null;
  } | null;
}

function deckLegendName(deck?: MatchLegendLookupDeck | null): string | null {
  return deck?.legend?.name?.trim() || null;
}

function deckLegendIdentity(deck?: MatchLegendLookupDeck | null): string | null {
  return legendIdentityFromRef(deck?.legend) || null;
}

/** Card titles like "Ornn - Fire Below…" / "Irelia, Blade Dancer", not list names like "Ornn aggro". */
function isLegendPrintTitle(label: string, identity: string): boolean {
  const trimmed = label.trim();
  if (!trimmed || legendIdentityFromRef({ name: trimmed }) !== identity) return false;
  return trimmed.toLowerCase() === identity || /[,]|(\s[-–—]\s)/.test(trimmed);
}

function legendPrintIdentity(label?: string | null): string | null {
  const trimmed = label?.trim();
  if (!trimmed || isUnknownMatchupLabel(trimmed)) return null;
  const identity = legendIdentityFromRef({ name: trimmed });
  return identity && isLegendPrintTitle(trimmed, identity) ? identity : null;
}

function deckLegendCover(deck?: MatchLegendLookupDeck | null): string | null {
  return deck?.legend?.thumbnailUrl || deck?.legend?.imageUrl || null;
}

function lookupDeck(decks: MatchLegendLookupDeck[], deckId?: string | null): MatchLegendLookupDeck | undefined {
  return deckId ? decks.find((deck) => deck.id === deckId) : undefined;
}

export function groupStatsByDeck(
  entries: UserMatchHistoryEntry[],
  decks: MatchLegendLookupDeck[] = [],
) {
  const grouped = new Map<string, { deckId: string | null; deckTitle: string; entries: UserMatchHistoryEntry[] }>();
  for (const entry of entries) {
    if (!entry.myDeckId && isUnlabeledDeckTitle(entry.myDeckTitle)) continue;
    const key = entry.myDeckId || entry.myDeckTitle || NO_DECK_LABEL;
    const existing = grouped.get(key);
    if (existing) {
      existing.entries.push(entry);
    } else {
      grouped.set(key, {
        deckId: entry.myDeckId ?? null,
        deckTitle: displayDeckLabel(entry.myDeckTitle),
        entries: [entry],
      });
    }
  }
  return [...grouped.values()].map((group) => {
    const myDeck = lookupDeck(decks, group.deckId);
    const deckTitle = isUnlabeledDeckTitle(group.deckTitle)
      ? (myDeck?.title?.trim() || NO_DECK_LABEL)
      : group.deckTitle;
    const legendName = deckLegendName(myDeck);
    return {
      ...group,
      deckTitle,
      detail: legendName && legendName !== deckTitle ? legendName : null,
      coverUrl: firstCoverUrl(group.entries, 'myDeckCoverUrl') || deckLegendCover(myDeck),
      stats: calculateMatchStats(group.entries),
    };
  });
}

/** Collapse owned decks under their legend so the insight table stays short. */
export function groupStatsByMyLegend(
  entries: UserMatchHistoryEntry[],
  decks: MatchLegendLookupDeck[] = [],
) {
  const grouped = new Map<string, {
    label: string;
    identity: string | null;
    deckIds: Set<string>;
    deckTitles: Set<string>;
    coverUrl: string | null;
    entries: UserMatchHistoryEntry[];
  }>();

  for (const entry of entries) {
    const myDeck = lookupDeck(decks, entry.myDeckId);
    const identity = deckLegendIdentity(myDeck);
    if (!identity && !entry.myDeckId && isUnlabeledDeckTitle(entry.myDeckTitle)) continue;
    const fallbackTitle = isUnlabeledDeckTitle(entry.myDeckTitle) ? NO_DECK_LABEL : (entry.myDeckTitle?.trim() || NO_DECK_LABEL);
    const label = identity ? legendDisplayName(identity) : fallbackTitle;
    const key = identity ? `legend:${identity}` : `deck:${entry.myDeckId || fallbackTitle}`;
    const coverUrl = entry.myDeckCoverUrl || deckLegendCover(myDeck) || null;
    const existing = grouped.get(key);
    if (existing) {
      existing.entries.push(entry);
      if (entry.myDeckId) existing.deckIds.add(entry.myDeckId);
      if (!isUnlabeledDeckTitle(entry.myDeckTitle)) existing.deckTitles.add(entry.myDeckTitle!.trim());
      if (!existing.coverUrl && coverUrl) existing.coverUrl = coverUrl;
    } else {
      grouped.set(key, {
        label,
        identity,
        deckIds: new Set(entry.myDeckId ? [entry.myDeckId] : []),
        deckTitles: new Set(isUnlabeledDeckTitle(entry.myDeckTitle) ? [] : [entry.myDeckTitle!.trim()]),
        coverUrl,
        entries: [entry],
      });
    }
  }

  return [...grouped.values()].map((group) => {
    const titles = [...group.deckTitles];
    const deckIds = [...group.deckIds];
    const detail = titles.length > 1
      ? `${titles.length} decks`
      : titles[0] && titles[0] !== group.label
        ? titles[0]
        : null;
    return {
      label: group.label,
      detail,
      identity: group.identity,
      deckId: deckIds.length === 1 ? deckIds[0] : null,
      coverUrl: group.coverUrl,
      stats: calculateMatchStats(group.entries),
    };
  });
}

export function groupStatsByMatchup(
  entries: UserMatchHistoryEntry[],
  decks: MatchLegendLookupDeck[] = [],
) {
  const grouped = new Map<string, {
    label: string;
    details: Set<string>;
    coverUrl: string | null;
    entries: UserMatchHistoryEntry[];
  }>();

  for (const entry of entries) {
    const opponentDeck = lookupDeck(decks, entry.opponentDeckId);
    const rawLegendName = entry.opponentLegendName?.trim() || deckLegendName(opponentDeck) || null;
    const identity = legendIdentityFromRef({
      name: rawLegendName,
      champion: opponentDeck?.legend?.champion,
    }) || deckLegendIdentity(opponentDeck) || legendPrintIdentity(entry.opponentDeckLabel);
    const legendLabel = identity ? legendDisplayName(identity) : rawLegendName;
    const deckLabel = isUnknownMatchupLabel(entry.opponentDeckLabel) ? null : entry.opponentDeckLabel!.trim();
    const deckIsLegendPrint = Boolean(deckLabel && identity && isLegendPrintTitle(deckLabel, identity));
    const hasNamedDeck = Boolean(deckLabel && !deckIsLegendPrint && deckLabel !== rawLegendName && deckLabel !== legendLabel);
    const label = hasNamedDeck ? deckLabel : legendLabel;
    if (!label) continue;

    const key = hasNamedDeck ? `deck:${deckLabel}` : `legend:${identity || legendLabel}`;
    const extra = hasNamedDeck ? legendLabel : null;
    const coverUrl = entry.opponentDeckCoverUrl || deckLegendCover(opponentDeck) || null;
    const existing = grouped.get(key);
    if (existing) {
      existing.entries.push(entry);
      if (extra && extra !== label) existing.details.add(extra);
      if (!existing.coverUrl && coverUrl) existing.coverUrl = coverUrl;
    } else {
      grouped.set(key, {
        label,
        details: new Set(extra && extra !== label ? [extra] : []),
        coverUrl,
        entries: [entry],
      });
    }
  }

  return [...grouped.values()].map((group) => {
    const details = [...group.details];
    return {
      label: group.label,
      detail: details.length > 1 ? `${details.length} decks` : details[0] ?? null,
      coverUrl: group.coverUrl,
      stats: calculateMatchStats(group.entries),
    };
  });
}
